import { editionFor, editionsFor, nameSpans, readerFor } from '@versesverse/core';
import { originalLangOfRef } from '@versesverse/schema/aramaic';
import { sectionKey } from '@versesverse/schema/sections';
import type { VerseNote } from '@versesverse/schema/types';
import type { EditionNote, NameForms, NameSpan } from '@versesverse/core';

/*
 * A RANGE OF LOADED TEXT, TURNED INTO PASSAGES A PANEL CAN RENDER.
 *
 * Pure: given the verse ids to show, a way to read each one's text, and the bundle's reference and
 * corpus tables, it produces what a reader sees — the citation, the English, the original beside
 * it (with the language and direction a screen reader and a browser both need), whose translation
 * this is and on what terms, and the one named link out to read it at its source and disagree. The
 * fetching is someone else's job; this is only the shaping, so it can be tested without a network.
 */
/** One English rendering of one verse, with the name a reader must be shown beside it. */
export interface PassageRendering {
  /** Stable id — `primary`, `second`, `third`, or a translation's own key once texts migrate. */
  id: string;
  /** Whose words these are. Never blank: a rendering with no name cannot be shown. */
  name: string;
  text: string;
  /** The names inside this rendering that travel to their own verses — every rendering is a door. */
  links: NameSpan[];
  /*
   * WHAT LANGUAGE THIS RENDERING IS IN, and which way it runs.
   *
   * Every rendering was English until 2026-08-16, so the page could lay them all out one way and
   * be right about all of them. The Arabic, Salkinson's Hebrew, the Coptic and the Armenian ended
   * that, and two of the four read right to left.
   *
   * The ORIGINAL has carried `lang` and `rtl` since the Hebrew arrived and the page uses both. The
   * renderings carried neither, so choosing Smith and Van Dyck would have shown Arabic laid out
   * left to right — not a subtle defect — with no `lang` for a screen reader to pronounce it by.
   *
   * Undefined means English and left to right, which is what the other five renderings still are.
   */
  lang?: string;
  rtl?: boolean;
}

export interface Passage {
  id: number;
  ref: string;
  corpusId: string;
  text: string;
  original?: string;
  /** BCP-47 language of the original, for a `lang` attribute — the a11y carry-over for 57 passages. */
  originalLang?: string;
  originalRtl?: boolean;
  /** Where to read this passage elsewhere, or null if its text names nowhere. */
  source: string | null;
  /** The NAME of that destination — BibleGateway, quran.com, Sefaria — never a generic "elsewhere". */
  sourceName: string | null;
  /** Whose English this is, and on what licence — the attribution the terms require, not a bare link. */
  edition: EditionNote | null;
  /** Where a name the map knows sits in `text`, so the reader can travel by word to that name's verses. */
  links: NameSpan[];
  /**
   * WHOSE WORDS THE MAIN TEXT IS — the caption that was never there.
   *
   * `altName` and `alt2Name` have always existed, so a reader could tell whose words the SECOND
   * and THIRD translations were, while the one they were actually reading was anonymous. That is
   * the wrong way round: a caption is a claim about who wrote the words above it, and the text
   * most people read carried none.
   *
   * Optional only because a bundle built before this shipped does not carry it, and losing the
   * scripture to a missing caption would be a far worse failure than an unnamed rendering. The
   * schema suite asserts every shipped corpus names its translations.
   */
  translationName?: string;
  /**
   * The fourth English and beyond, already paired with the names the corpus declares.
   *
   * Kept as a list rather than a map so the corpus's declared ORDER survives to the reader — the
   * order translations are offered in is a small editorial claim, and an object's key order is
   * not something to rest one on.
   */
  extraRenderings?: PassageRendering[];
  /** A second English translation, where the text carries one — so no single translator's wording drives it. */
  alt?: string;
  /** Who made that second translation (ASV, Yusuf Ali) — a caption is a claim about whose words these are. */
  altName?: string;
  alt2?: string;
  alt2Name?: string;
  /** Which collection a hadith is from and what grade it carries — hadith only. */
  provenance?: string;
  /** The clickable names inside the second translation — it is a door as much as the first. */
  altLinks: NameSpan[];
  alt2Links: NameSpan[];
  /**
   * What the manuscripts say, where this edition prints a passage the earliest copies lack.
   *
   * The King James carries the long ending of Mark, the heavenly witnesses at 1 John 5:7 and the
   * woman taken in adultery, and marks none of them. Without this the reader is told nothing.
   */
  /** What this passage is called where its reference does not say — see schema/sections.ts. */
  section?: string;
  /** Who narrated this report and how it is graded, where its source says — see schema/types.ts. */
  note?: VerseNote;
  /** The original edition that has no text here, where a passage carries no original at all. */
  originalAbsent?: string;
  textualNote?: string;
}

/**
 * EVERY ENGLISH A PASSAGE CARRIES, NAMED, IN THE ORDER THE CORPUS DECLARES THEM.
 *
 * Derived rather than stored, so a `Passage` stays the shape it was and the renderings cannot
 * drift out of step with the fields they come from. The aim behind it is not tidiness: a verse
 * had three renderings because somebody added a field three times, and what is wanted is as many
 * as can be licensed — because **where two translators differ, the difference is the evidence**.
 * Isaiah 7:14 is "a virgin" in the King James and "the young woman" in the JPS, and that one word
 * is most of what two traditions have said to each other for two thousand years.
 *
 * A RENDERING NEEDS BOTH A NAME AND TEXT. Text with no name cannot be captioned, and this project
 * does not show words without saying whose they are; a name with no text would render an empty
 * paragraph attributed to a translator who wrote nothing there. Either half missing means there is
 * no rendering to offer, which is a truthful answer rather than a gap to paper over.
 *
 * THE LINKS ARE THE ONES THAT BELONG TO EACH RENDERING. `links`, `altLinks` and `alt2Links` are
 * offsets into three different strings, and the King James and the JPS do not put "Isaiah" in the
 * same place. Carrying one rendering's spans onto another's words would underline the wrong ones
 * — which looks like a styling glitch and is in fact a wrong claim about where a name appears.
 */
export function renderingsOf(passage: Passage): PassageRendering[] {
  const out: PassageRendering[] = [];
  const add = (
    id: string, name: string | undefined, text: string | undefined, links: NameSpan[],
    lang?: string, rtl?: boolean,
  ): void => {
    if (!name?.trim() || !text?.trim()) return;
    out.push({ id, name, text, links, lang, rtl });
  };
  add('primary', passage.translationName, passage.text, passage.links);
  add('second', passage.altName, passage.alt, passage.altLinks);
  add('third', passage.alt2Name, passage.alt2, passage.alt2Links);
  /*
   * The fourth and beyond, already named and in the corpus's declared order. Appended rather than
   * interleaved, so gaining a translation never moves one a reader has already chosen.
   *
   * `lang` AND `rtl` TRAVEL WITH THEM, and forgetting to pass them here was the whole bug: the
   * corpus declared Arabic right-to-left, `passages.ts` carried it onto `extraRenderings`, and
   * this function dropped both on the floor while building the list the page actually reads. The
   * page then set no direction and the Arabic came out left to right — declared correctly at
   * every step but one, which is the shape of nearly every defect this project has found.
   *
   * The first three are English by construction: they are `translation`, `secondTranslation` and
   * `thirdTranslation`, and every one of them in the registry is an English edition.
   */
  for (const extra of passage.extraRenderings ?? []) {
    add(extra.id, extra.name, extra.text, extra.links, extra.lang, extra.rtl);
  }
  /*
   * TEXT WITHOUT A NAME IS STILL SHOWN, uncaptioned, rather than lost.
   *
   * The rule above — no rendering without a name — is right for the ALTERNATIVES, where an
   * uncaptioned second English is meaningless: a reader cannot weigh it against the first without
   * knowing whose it is. Applied to the main text it is a different act entirely. It deletes
   * scripture from the page because a caption was missing.
   *
   * This was written, then dropped in a refactor, and the test suite caught it: fixtures carrying
   * `text` and no `translationName` rendered "this passage did not load" over real words. In
   * production the bundle names every corpus's translation, so the branch is unreachable there —
   * which is exactly why it needs to exist. An unreachable safety net costs nothing; the failure
   * it prevents is a reader told a verse does not exist.
   */
  if (out.length === 0 && passage.text.trim()) {
    out.push({ id: 'primary', name: '', text: passage.text, links: passage.links });
  }
  return out;
}

/**
 * Which rendering to show, given the translation this reader prefers across the whole library.
 *
 * MATCHED BY NAME, NOT BY ID, and that is what makes one choice reach every text. The ids are
 * positional — `primary`, `second`, `third` — and mean different things in different books:
 * `third` is the JPS in the Hebrew Bible and nothing in the Quran. Carrying an id across the
 * library would show a reader whatever happened to be third wherever they went, which is not a
 * preference but a coincidence. A name is the same translation everywhere it appears, so choosing
 * the King James once reaches the Hebrew Bible and the New Testament both.
 *
 * A PASSAGE THAT LACKS THE CHOSEN TRANSLATION FALLS BACK TO ITS OWN FIRST. It must: the JPS
 * covers the Hebrew Bible and nothing else, so a reader who prefers it still has to be able to
 * read the Quran. Returning nothing would blank the verse, and returning the preferred NAME over
 * whatever text was to hand would be a false attribution — one translator's words under another's
 * name, in real English, undetectable by the reader. The caption always names what is shown.
 */
export function chooseRendering(renderings: PassageRendering[], preferredName: string | null | undefined): PassageRendering | undefined {
  if (preferredName) {
    const wanted = renderings.find(r => r.name === preferredName);
    if (wanted) return wanted;
  }
  return renderings[0];
}

/** How to read one verse's loaded text — `undefined` while its range has not arrived. */
export type TextOf = (id: number) => {
  eng?: string;
  orig?: string;
  alt?: string;
  alt2?: string;
  prov?: string;
  mentions?: number[];
  /** The fourth English and beyond, by translation id — see `Verse.texts`. */
  texts?: Record<string, string>;
} | undefined;

/** What {@link buildPassages} reads about a text — narrower than Corpus, which is assignable to it. */
export interface CorpusRef {
  id: string;
  code: string;
  readOnline?: string | null;
  original?: { lang?: string; rtl?: boolean };
  /**
   * The name of the FIRST English — the one most readers actually read.
   *
   * It was not here, because the primary text rendered without a caption and nothing needed it.
   * That is the omission, not an oversight in this interface: the second and third translations
   * were named on screen and the main one was anonymous, so a reader could tell whose words the
   * alternatives were and not whose words they were reading.
   */
  translation?: string;
  /** The name of the second English translation, where this text has one. Absent → no verse holds `alt`. */
  secondTranslation?: string;
  /** The third, which only the Hebrew Bible has. Absent → no verse holds `alt2`. */
  thirdTranslation?: string;
  /**
   * The fourth English and beyond, named — id and the caption that must appear under it.
   *
   * The three slots above are three because somebody added a field three times. A text gaining a
   * Catholic English, or a second rendering of the Quran, is declared here instead, and a verse's
   * `texts` map is keyed to match. An id with no entry here has no caption, so it cannot be shown.
   */
  extraTranslations?: { id: string; name: string; lang?: string; rtl?: boolean }[];
  /** What bounds "the passage around a verse" — its book, or its chapter/surah. Default book. */
  passageUnit?: 'book' | 'chapter';
}

/** The bundle tables a passage is built from — narrower than Bundle. */
export interface PassageTables {
  refs: string[];
  /** One corpus code per verse id. */
  corpusOf: string;
  corpora: CorpusRef[];
  /** `${corpusId}|${book}` → [englishEditionIndex, originalEditionIndex?] — which translation each book is in. */
  bookEditions?: Record<string, number[]>;
  /** The translations themselves, indexed by the numbers in `bookEditions`. */
  editions?: Partial<EditionNote>[];
  /** Every name and its spellings, indexed by node position — so a verse's `mentions` (node indices)
   *  can be resolved to the words to make clickable. Absent means no linking (plain text). */
  names?: NameForms[];
  /** Key from sectionKey -> what the passage is called where its reference does not say. */
  sections?: Record<string, string>;
  /** Verse id -> what its source states about its standing, straight from the bundle. */
  verseNotes?: Record<number, VerseNote>;
  /** Verse ref -> manuscript note, straight from the bundle. */
  textualNotes?: Record<string, string>;
}

/*
 * The reader's chosen translation is deliberately NOT a parameter here.
 *
 * It was one for about ten minutes. Shaping is pure and testable without a browser, and a
 * preference is neither — it lives in storage, changes without the bundle changing, and differs
 * between two people reading the same verse. Passing it in would make a passage's contents depend
 * on who is looking, which is exactly the coupling that makes a render layer hard to reason about.
 *
 * So this produces every rendering the verse has, `renderingsOf` names them, and `chooseRendering`
 * picks one at the point of display where the reader's preference actually lives.
 */
export function buildPassages(ids: number[], textOf: TextOf, tables: PassageTables): Passage[] {
  const byCode = new Map(tables.corpora.map(c => [c.code, c]));
  const passages: Passage[] = [];
  for (const id of ids) {
    const ref = tables.refs[id];
    if (!ref) continue; // an id with no citation is not a passage a reader can be shown
    const corpus = byCode.get(tables.corpusOf[id] ?? '');
    const loaded = textOf(id) ?? {};
    /* One lookup for both the destination and its name, so the link can say where it goes. */
    const reader = corpus ? readerFor(corpus.readOnline) : null;
    const english = loaded.eng ?? '';
    /* The names this verse mentions (node indices from the chunk) resolved to their spellings, then
       located in the English — so each becomes a door to that name's own verses. Narrowed by the
       build's own mention list, not a scan of every name in scripture. */
    const mentioned = (tables.names && loaded.mentions?.length)
      ? loaded.mentions.map(i => tables.names![i]).filter((f): f is NameForms => f !== undefined)
      : [];
    /* The second English, only where the text actually carries one — a verse holding `alt` text for
       a corpus with no named second translation would be captionless, and the schema forbids it. */
    const alt = (corpus?.secondTranslation && loaded.alt) ? loaded.alt : undefined;
    /* And the third, on the same rule: only where the corpus names one. The Hebrew Bible is
       the only text that does, and its third is the Jewish English. */
    const alt2 = (corpus?.thirdTranslation && loaded.alt2) ? loaded.alt2 : undefined;
    /*
     * The fourth English and beyond, paired with the names the corpus declares for them.
     *
     * Driven by the CORPUS's list rather than by the verse's keys, and in that order, for two
     * reasons. A key the corpus does not name has no caption, and this project does not show
     * words without saying whose they are — iterating the verse would let an unnamed rendering
     * through. And the corpus's order is the order a reader meets them in, which is an editorial
     * decision rather than whatever order the fetcher happened to write.
     */
    const extraRenderings: PassageRendering[] = [];
    for (const declared of corpus?.extraTranslations ?? []) {
      const body = loaded.texts?.[declared.id];
      if (!body) continue;
      extraRenderings.push({
        id: declared.id,
        name: declared.name,
        text: body,
        /* Its own spans: offsets belong to one string, and two translations do not put a name in
           the same place. See `renderingsOf`. */
        links: mentioned.length ? nameSpans(body, mentioned) : [],
        /* Carried from the corpus rather than guessed from the text: a rendering knows what
           language it is, and sniffing a script would be wrong for the first Coptic verse that
           happens to quote a Greek word. */
        lang: declared.lang,
        rtl: declared.rtl,
      });
    }

    passages.push({
      id,
      ref,
      corpusId: corpus?.id ?? '',
      text: english,
      translationName: corpus?.translation,
      extraRenderings: extraRenderings.length ? extraRenderings : undefined,
      original: loaded.orig || undefined,
      /*
       * THE LANGUAGE THIS VERSE'S ORIGINAL IS ACTUALLY IN, which is not always the corpus's.
       *
       * Daniel 2:4-7:28, Ezra 4:8-6:18 and 7:12-26 and Jeremiah 10:11 are Aramaic inside a corpus
       * that declares Hebrew — 263 passages. The static pages learned this first; a reader in the
       * app was still told Aramaic was Hebrew, which is the larger surface of the two.
       * `schema/aramaic.ts` holds the sections and why they are declared rather than detected.
       */
      originalLang: originalLangOfRef(corpus?.id ?? '', ref) ?? corpus?.original?.lang ?? undefined,
      originalRtl: corpus?.original?.rtl ?? undefined,
      source: reader ? reader.url(ref) : null,
      sourceName: reader?.name ?? null,
      edition: corpus ? editionFor(corpus.id, ref, tables.bookEditions, tables.editions) : null,
      links: mentioned.length ? nameSpans(english, mentioned) : [],
      alt,
      section: tables.sections?.[sectionKey(corpus?.id ?? '', ref)],
      note: tables.verseNotes?.[id],
      /*
       * WHICH EDITION IS SILENT, where this passage has no original at all.
       *
       * Shown in English alone a verse reads as badly built when it is a textual fact: Nestle 1904
       * has no Matthew 17:21 because the earliest manuscripts lack it. 182 passages across the
       * library carry no original and the reasons differ — Joel is numbered differently in Hebrew
       * and its partner falls in another chapter — so this names the edition and claims no cause.
       */
      originalAbsent: loaded.orig?.trim()
        ? undefined
        : editionsFor(corpus?.id ?? '', ref, tables.bookEditions, tables.editions).original?.title,
      textualNote: tables.textualNotes?.[ref],
      altName: alt ? corpus?.secondTranslation : undefined,
      alt2,
      alt2Name: alt2 ? corpus?.thirdTranslation : undefined,
      provenance: loaded.prov,
      altLinks: alt && mentioned.length ? nameSpans(alt, mentioned) : [],
      alt2Links: alt2 && mentioned.length ? nameSpans(alt2, mentioned) : [],
    });
  }
  return passages;
}
